import React, { useEffect, useRef, useState } from "react";
import { Monitor, Moon, Sun, type LucideIcon } from "lucide-react";
import {
  useAppearance,
  type Appearance,
} from "@/shared/providers/AppearanceProvider";

interface OptionDef {
  value: Appearance;
  label: string;
  /** Lucide icon component shown next to the label and as the trigger
   *  when this option is the active one. */
  Icon: LucideIcon;
}

const OPTIONS: OptionDef[] = [
  { value: "light",  label: "Light",  Icon: Sun },
  { value: "dark",   label: "Dark",   Icon: Moon },
  { value: "system", label: "System", Icon: Monitor },
];

/**
 * Three-way appearance picker (Light / Dark / System) rendered as a
 * navbar icon-button + popover. The trigger icon reflects the user's
 * current PREFERENCE (not the resolved theme), so a user with
 * `system` always sees the monitor glyph regardless of the OS state.
 *
 * Lives in the navbar between the Search button and Switch-version
 * (or wherever it's mounted). Closes on Escape or click-outside.
 */
const AppearanceToggle: React.FC = () => {
  const { appearance, setAppearance } = useAppearance();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  const TriggerIcon = OPTIONS.find((o) => o.value === appearance)?.Icon ?? Monitor;
  const triggerLabel =
    appearance === "light"
      ? "Appearance: Light"
      : appearance === "dark"
        ? "Appearance: Dark"
        : "Appearance: System";

  return (
    // `navbar-tooltip navbar-tooltip-bottom` adds the hover tooltip below
    // the trigger (matches the scheduled-runs + alerts buttons). The
    // existing `relative` positioning is preserved for the dropdown
    // menu's `absolute right-0` anchoring — both classes coexist
    // without conflict.
    <div ref={wrapperRef} className="navbar-tooltip navbar-tooltip-bottom relative">
      <button
        type="button"
        className="text-on-primary p-1"
        aria-label={triggerLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        data-testid="appearance-toggle"
      >
        <TriggerIcon size={16} aria-hidden={true} />
      </button>
      {/* Tooltip — uses the active appearance for context so dispatchers
          know what state they're currently in. The aria-label on the
          button still carries the same info for screen-reader users. */}
      <span className="navbar-tooltip-text" role="tooltip">
        {triggerLabel}
      </span>

      {open && (
        <div
          role="menu"
          aria-label="Appearance options"
          className="absolute right-0 mt-2 w-44 rounded-md border border-border bg-surface shadow-lg z-[220] overflow-hidden"
        >
          {OPTIONS.map((opt) => {
            const isActive = appearance === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="menuitemradio"
                aria-checked={isActive}
                className={`flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-primary/10 text-primary font-medium"
                    : "text-text hover:bg-surface-alt"
                }`}
                onClick={() => {
                  setAppearance(opt.value);
                  setOpen(false);
                }}
                data-testid={`appearance-option-${opt.value}`}
              >
                <opt.Icon size={16} aria-hidden={true} className="shrink-0" />
                <span>{opt.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AppearanceToggle;
